import * as React from 'react'
import { FileIcon } from 'lucide-react'
import { cn } from '../../../lib/utils'

/**
 * File attachment bubble — thumbnail/icon + filename + size + download action.
 * Designed to fit inside a chat message. Uses the same bubble styling as
 * incoming ChatMessage variant.
 */
export interface ChatAttachmentProps extends React.HTMLAttributes<HTMLDivElement> {
	fileName: string
	fileSize?: string
	thumbnailUrl?: string
	time?: string
	downloadLabel?: string
	onDownload?: () => void
	align?: 'start' | 'end'
}

const ChatAttachment = React.forwardRef<HTMLDivElement, ChatAttachmentProps>(
	({ className, fileName, fileSize, thumbnailUrl, time, downloadLabel = 'Download', onDownload, align = 'start', ...props }, ref) => (
		<div
			ref={ref}
			data-slot="chat-attachment"
			className={cn(
				'flex w-fit max-w-[85%] items-center gap-3 rounded-(--radius-md) bg-(--color-muted) p-3',
				align === 'end' ? 'self-end' : 'self-start',
				className
			)}
			{...props}
		>
			{thumbnailUrl ? (
				<img
					src={thumbnailUrl}
					alt={fileName}
					className="size-10 shrink-0 rounded-(--radius-sm) object-cover"
				/>
			) : (
				<div className="flex size-10 shrink-0 items-center justify-center rounded-(--radius-sm) bg-(--color-background)">
					<FileIcon className="size-5 text-(--color-muted-foreground)" />
				</div>
			)}
			<div className="flex min-w-0 flex-1 flex-col gap-0.5">
				<p className="truncate text-sm font-[500] leading-5 text-(--color-foreground)">
					{fileName}
				</p>
				<div className="flex items-center gap-2 text-xs leading-4 text-(--color-muted-foreground)">
					{fileSize && <span>{fileSize}</span>}
					<button
						type="button"
						onClick={onDownload}
						className="text-(--color-info) hover:underline underline-offset-2"
					>
						{downloadLabel}
					</button>
				</div>
			</div>
			{time && (
				<span className="self-end text-xs leading-4 text-(--color-muted-foreground)">
					{time}
				</span>
			)}
		</div>
	)
)

ChatAttachment.displayName = 'ChatAttachment'
export { ChatAttachment }
